"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DATABASE_DATE_WITHOUT_TIME_ZONE = exports.DATABASE_STORED_TEXT_TIME_ZONE = void 0;
exports.parseDatabaseNumberText = parseDatabaseNumberText;
exports.parseDatabaseDateTimeWithOffset = parseDatabaseDateTimeWithOffset;
exports.isDatabaseDateText = isDatabaseDateText;
exports.databaseDateTextNeedsTimeZone = databaseDateTextNeedsTimeZone;
exports.parseDatabaseDateText = parseDatabaseDateText;
/**
 * Text parsers for database cell conversions. They read every supported
 * format alike on every device and on the server, so a converted value never
 * depends on where it is read.
 *
 * Numbers: US ("1,234.56") and German ("1.234,56") separators.
 * Dates: ISO "2025-07-31", "30.07.2025", US "07/31/2025" or "07-31-2025",
 * "July 31, 2025", "Sep 30, 2025", German "8. Juni 2025", British
 * "31 July 2025" are midnight, a date with a time "2025-07-31 09:30" or
 * "2025-07-31T09:30:00.000" is that wall time, both in the given IANA time
 * zone; a date-time with "Z" or an offset is its own moment in every zone.
 */
const zoned_wall_time_1 = require("./zoned-wall-time");
/** Parses number text; null when it is not a number. */
function parseDatabaseNumberText(text) {
    let t = text.trim().replace(/[€$£¥₹%\s ]/g, '');
    if (!t)
        return null;
    let sign = 1;
    if (t.startsWith('-')) {
        sign = -1;
        t = t.slice(1);
    }
    else if (t.startsWith('+')) {
        t = t.slice(1);
    }
    if (/^\d+$/.test(t))
        return sign * parseInt(t, 10);
    const lastDot = t.lastIndexOf('.');
    const lastComma = t.lastIndexOf(',');
    const hasDot = lastDot >= 0;
    const hasComma = lastComma >= 0;
    // Both present → last separator is decimal
    if (hasDot && hasComma) {
        if (lastDot > lastComma) {
            return toFloat(t.replace(/,/g, ''), sign);
        }
        return toFloat(t.replace(/\./g, '').replace(',', '.'), sign);
    }
    // Only comma
    if (hasComma && !hasDot) {
        if ((t.match(/,/g) || []).length > 1) {
            return toFloat(t.replace(/,/g, ''), sign);
        }
        const afterComma = t.slice(lastComma + 1);
        const beforeComma = t.slice(0, lastComma);
        if (afterComma.length === 3 &&
            /^\d+$/.test(afterComma) &&
            beforeComma.length >= 1) {
            return toFloat(t.replace(/,/g, ''), sign);
        }
        return toFloat(t.replace(',', '.'), sign);
    }
    // Only dot
    if (hasDot && !hasComma) {
        if ((t.match(/\./g) || []).length > 1) {
            return toFloat(t.replace(/\./g, ''), sign);
        }
        return toFloat(t, sign);
    }
    return null;
}
function toFloat(s, sign) {
    const n = parseFloat(s);
    if (isNaN(n))
        return null;
    return sign * n;
}
const ENGLISH_MONTHS = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12,
};
const SHORT_MONTHS = {
    Jan: 1,
    Feb: 2,
    Mar: 3,
    Apr: 4,
    May: 5,
    Jun: 6,
    Jul: 7,
    Aug: 8,
    Sep: 9,
    Oct: 10,
    Nov: 11,
    Dec: 12,
};
const GERMAN_MONTHS = {
    Januar: 1,
    Februar: 2,
    März: 3,
    April: 4,
    Mai: 5,
    Juni: 6,
    Juli: 7,
    August: 8,
    September: 9,
    Oktober: 10,
    November: 11,
    Dezember: 12,
};
/**
 * Time zone of date text that is read independent of its reader: a text cell
 * converted to a date is read again on every device, so it stays UTC.
 */
exports.DATABASE_STORED_TEXT_TIME_ZONE = 'UTC';
const DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3})\d*)?)?(Z|[+-]\d{2}:\d{2})?$/;
/**
 * Parses a date-time that names its zone ("…Z" or "…+01:00") to its moment
 * (ms); null for any other text, which needs a time zone to be read.
 */
function parseDatabaseDateTimeWithOffset(text) {
    const m = text.trim().match(DATE_TIME);
    if (!m?.[8])
        return null;
    const time = Date.parse(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] ?? '00'}.${(m[7] ?? '0').padEnd(3, '0')}${m[8]}`);
    return Number.isNaN(time) ? null : time;
}
function wallTime(timeZone, year, month, day, hour = 0, minute = 0, second = 0, millisecond = 0) {
    // The runtime rolls a day the month does not have into the next month.
    const check = new Date(Date.UTC(year, month - 1, day));
    if (check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day)
        return null;
    return (0, zoned_wall_time_1.zonedWallDateTimeToDate)({ year, month, day, hour, minute, second, millisecond }, timeZone).getTime();
}
/**
 * A day (a date without a time) is UTC midnight of its calendar day for every
 * writer and reader; null for a day the month does not have.
 */
function calendarDay(year, month, day) {
    // The runtime rolls a day the month does not have into the next month.
    const time = Date.UTC(year, month - 1, day);
    const check = new Date(time);
    return check.getUTCMonth() === month - 1 && check.getUTCDate() === day
        ? time
        : null;
}
/** Refusal of a wall time that needs the writer's time zone when none is known. */
exports.DATABASE_DATE_WITHOUT_TIME_ZONE = 'No time zone is known for this user. Write the time with an offset (e.g. 2026-10-28T09:00+01:00) or write the date without a time, or open StreamLain once so it learns the time zone';
/** True for text in one of the date formats. */
function isDatabaseDateText(text) {
    return parseDatabaseDateText(text, exports.DATABASE_STORED_TEXT_TIME_ZONE) !== null;
}
/** True for a date-time without offset: only a time zone gives its moment. */
function databaseDateTextNeedsTimeZone(text) {
    const m = text.trim().match(DATE_TIME);
    return m !== null && !m[8];
}
/**
 * Parses date text to a timestamp (ms): a date is UTC midnight of its
 * calendar day, a date-time without offset is that wall time in `timeZone`;
 * null when unknown.
 */
function parseDatabaseDateText(text, timeZone) {
    const t = text.trim();
    if (!t)
        return null;
    let m;
    m = t.match(DATE_TIME);
    if (m) {
        if (m[8])
            return parseDatabaseDateTimeWithOffset(t);
        return wallTime(timeZone, Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] ?? 0), Number((m[7] ?? '0').padEnd(3, '0')));
    }
    m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m)
        return calendarDay(Number(m[1]), Number(m[2]), Number(m[3]));
    m = t.match(/^(\d{1,2})\.(\d{2})\.(\d{4})$/);
    if (m)
        return calendarDay(Number(m[3]), Number(m[2]), Number(m[1]));
    m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m)
        return calendarDay(Number(m[3]), Number(m[1]), Number(m[2]));
    m = t.match(/^(\d{1,2})\.\s*(\p{L}+)\s+(\d{4})$/u);
    if (m?.[2]) {
        const mo = GERMAN_MONTHS[m[2]];
        if (mo)
            return calendarDay(Number(m[3]), mo, Number(m[1]));
    }
    m = t.match(/^(\p{L}+)\s+(\d{1,2}),?\s+(\d{4})$/u);
    if (m?.[1]) {
        const mo = ENGLISH_MONTHS[m[1]] ?? SHORT_MONTHS[m[1]];
        if (mo)
            return calendarDay(Number(m[3]), mo, Number(m[2]));
    }
    m = t.match(/^(\d{1,2})\s+(\p{L}+)\s+(\d{4})$/u);
    if (m?.[2]) {
        const mo = ENGLISH_MONTHS[m[2]] ?? SHORT_MONTHS[m[2]];
        if (mo)
            return calendarDay(Number(m[3]), mo, Number(m[1]));
    }
    return null;
}
